import { Program } from '@citruscamps/citrus-client'
import { useFormik } from 'formik'
import { default as React, useState } from 'react'
import { NavLink } from 'react-router-dom'
import * as Yup from 'yup'
import { Checkbox } from '../../../../../components/Checkbox/Checkbox'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { ROUTES } from '../../../../../constants/routes'
import translations from '../../../../../translations/en.json'
import { formatList } from '../../../../../utils/formatters'
import { IBulkImportCreateOptions, ImportItemType } from '../hooks/useHandleImport'

interface IValue extends IBulkImportCreateOptions {
  send_invite?: boolean
}

interface IProps {
  program: Program
  itemTypes: ImportItemType[]
  initialValues: Partial<IValue>
  onBack?: () => Promise<void> | void
  onSubmit: (values: IBulkImportCreateOptions) => Promise<void> | void
}

export const ReviewImportStep = ({
  program,
  initialValues,
  itemTypes,
  onBack: handleBack,
  onSubmit,
}: IProps) => {
  const [errorMessage, setErrorMessage] = useState<string>()
  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldError,
    touched,
    values,
  } = useFormik<IValue>({
    initialValues: {
      itemTypes: initialValues?.itemTypes || [],
      contacts:
        initialValues?.contacts?.filter(
          (f, i, self) => i === self.findIndex((s) => f.email === s.email),
        ) || [],
      attendees: initialValues?.attendees || [],
      registrations: initialValues?.registrations || [],
      events: initialValues?.events || [],
      notes: initialValues?.notes || [],
      send_invite: typeof initialValues.send_invite === 'undefined' || initialValues.send_invite,
    },
    validationSchema: Yup.object().shape({
      itemTypes: Yup.array()
        .of(
          Yup.mixed<ImportItemType>().oneOf(
            ['contacts', 'attendees', 'registrations', 'events', 'notes'],
            (translations as any)['error.default_inputs_message'],
          ),
        )
        .min(1, (translations as any)['error.default_inputs_message']),
      contacts: Yup.array().when('itemTypes', {
        is: (val: ImportItemType[]) => val.includes('contacts'),
        then: Yup.array().min(1, (translations as any)['error.default_inputs_message']),
      }),
      attendees: Yup.array().when('itemTypes', {
        is: (val: ImportItemType[]) => val.includes('attendees'),
        then: Yup.array().min(1, (translations as any)['error.default_inputs_message']),
      }),
    }),
    onSubmit: async (value, { setSubmitting }) => {
      try {
        await onSubmit({
          itemTypes: value.itemTypes,
          contacts: value.contacts.map((c) => ({ ...c, send_invite: value.send_invite })),
          attendees: value.attendees.map((a) => ({ ...a, send_invite: value.send_invite })),
          registrations: value.registrations,
          events: value.events,
          notes: value.notes,
        })
      } catch (e: any) {
        if (e.param) {
          setFieldError(e.param, e.message)
        } else {
          setErrorMessage(e?.message)
        }
      }
      setSubmitting(false)
    },
  })

  const totalCount: number =
    (values.contacts?.length || 0) +
    (values.attendees?.length || 0) +
    (values.registrations?.length || 0) +
    (values.events?.length || 0) +
    (values.notes?.length || 0)

  return (
    <form className="py-3" onSubmit={handleSubmit}>
      <h5 className="mb-4">Review and complete your import</h5>
      <p>
        <b>{totalCount}</b> {formatList(itemTypes, 'and')} will be added to {program.name}.
      </p>
      <div className="mb-3">
        <Checkbox
          name="send_invite"
          label="Send invite to join Citrus"
          inputSize="sm"
          checked={values.send_invite}
          onChange={handleChange}
          onBlur={handleBlur}
          className={
            'custom-control-input' +
            (touched.send_invite && !!errors.send_invite ? ' is-invalid' : '')
          }
        />
        <div className="invalid-feedback d-block">
          {touched?.send_invite && !!errors?.send_invite
            ? errors?.send_invite
            : errorMessage || '\u00A0'}
        </div>
      </div>
      <div className="mb-3 d-flex justify-content-between mt-4">
        {handleBack ? (
          <button
            type="button"
            className="btn btn-link btn-block d-md-inline-block w-md-auto mt-2 px-0"
            onClick={() => handleBack?.()}
          >
            Back
          </button>
        ) : (
          <NavLink
            className="btn btn-link btn-block d-md-inline-block w-md-auto mt-2"
            to={ROUTES.DASHBOARD}
          >
            Cancel
          </NavLink>
        )}
        <LoadingButton
          type="submit"
          className="btn btn-primary btn-block d-md-inline-block w-md-auto"
          loading={isSubmitting}
        >
          Complete Import
        </LoadingButton>
      </div>
    </form>
  )
}
